import {
  Button,
  DefaultModalDialog,
  Heading,
  LabeledText,
  SelectField,
  useToast,
  Text,
  Link,
} from '~/common/components/defaultUIComponents';
import {
  Box,
  Flex,
  GridItem,
  SimpleGrid,
  Spinner,
  Stack,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  NewJobApi,
  NewJobRecord,
} from '~/common/api/zeiterfassungServerApi/NewJobApi';
import RecordPictureApi from '~/common/api/zeiterfassungServerApi/RecordPicApi';
import { ImageList } from '~/common/components/defaultUIComponents/ImageList';
import DepartmentApi from '~/common/api/zeiterfassungServerApi/DepartmentApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { JobApi } from '~/common/api/zeiterfassungServerApi/JobApi';
import { formatName } from '~/common/utils/employeeUtils';
import { DeleteIcon, ViewIcon } from '@chakra-ui/icons';
import { ImageListType } from 'react-images-uploading';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import { Paper } from '~/common/components/Paper';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import * as Yup from 'yup';

interface NewJobBoxProps {
  record: NewJobRecord;
}

const useNewJobRecord = (record: NewJobRecord) => {
  const modalDisclosure = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast({
    isClosable: true,
    title: 'dashboard.newjobs.create.error',
    status: 'error',
  });

  const [{ data: projects }] = ProjectApi.hooks.useProjectsQuery();
  const { mutateAsync: deleteAsync } = NewJobApi.hooks.useDeleteJobRecord();
  const { mutateAsync: createJobFromRecord } =
    NewJobApi.hooks.useCreateJobFromRecord();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const deleteNewJob = React.useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteAsync(record);
    } finally {
      setIsDeleting(false);
    }
  }, [record, deleteAsync]);
  const project = React.useMemo(
    () =>
      projects?.find(
        (project) =>
          project.id === (record.new_job.project as unknown as number),
      ),
    [record, projects],
  );
  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const employee = React.useMemo(
    () =>
      employees?.find((employee) => parseInt(employee.id) == record.employee),
    [record, employees],
  );
  const [{ data: departments }] = DepartmentApi.hooks.useDepartmentsQuery({
    enabled: modalDisclosure.isOpen,
  });
  const addNewJob = (values: { department: number; project: number }) => {
    return createJobFromRecord({
      id: record.id,
      department: values.department,
      project: values.project,
    })
      .then((job) =>
        navigate(routes.EDIT_JOB.replace(':id', job?.id.toString() ?? '0')),
      )
      .catch(() => toast({}));
  };
  const [imageList, setImageList] = useState<ImageListType>();
  RecordPictureApi.hooks.usePicturesOfNewJobRecord(record, {
    enabled: modalDisclosure.isOpen,
    onSuccess(data) {
      setImageList(data?.map((record) => ({ dataURL: record.picture })));
    },
  });
  const [{ data: job }] = JobApi.hooks.useGetJobQuery({ id: record.job });
  return {
    employee,
    project,
    deleteNewJob,
    modalDisclosure,
    addNewJob,
    departments,
    imageList,
    projects,
    job,
    isDeleting,
  };
};
const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export const NewJobListItem: React.FC<NewJobBoxProps> = ({ record }) => {
  const translate = useTranslate();
  const {
    employee,
    project,
    deleteNewJob,
    modalDisclosure,
    addNewJob,
    departments,
    imageList,
    projects,
    job,
    isDeleting,
  } = useNewJobRecord(record);

  return (
    <>
      <DefaultModalDialog
        alignFooterLeft={true}
        initialValues={{
          department: job?.department?.id ? job.department.id : 0,
          project: project ? project.id : 0,
        }}
        onSubmit={addNewJob}
        isOpen={modalDisclosure.isOpen}
        onCancel={modalDisclosure.onClose}
        dialogHeaderText={
          <Flex>
            <Text mr={3}>dashboard.newjobs.add.to</Text>
            <Link
              href={routes.PROJECT.replace(
                ':id',
                project?.id.toString() ?? '0',
              )}
            >
              {project?.name}
            </Link>
          </Flex>
        }
        submitText="dashboard.newjobs.add"
        showCancelButton
        size="4xl"
        validationSchema={Yup.object().shape({
          department: Yup.mixed()
            .required()
            .not(
              [-1, '-1', 0, '0'],
              'dashboard.newjobs.update.department.required',
            ),
        })}
        validateOnChange
        customSubmitButton={
          <SimpleGrid gap={2} mr={2} columns={2}>
            <Button type="submit">dashboard.newjobs.add</Button>
            <Button
              onClick={() => {
                void deleteNewJob();
              }}
              backgroundColor="red.600"
            >
              dashboard.newjobs.delete
            </Button>
          </SimpleGrid>
        }
      >
        <SimpleGrid columns={2} gap={3}>
          <Stack>
            <GridItem>
              <LabeledText spaceBetween label="dashboard.newjobs.name">
                {record.new_job.name}
              </LabeledText>
            </GridItem>
            <GridItem>
              <LabeledText spaceBetween label="dashboard.newjobs.employee">
                {formatName(employee)}
              </LabeledText>
            </GridItem>
            {record.comment && (
              <GridItem>
                <LabeledText col label="dashboard.newjobs.description">
                  {record.comment}
                </LabeledText>
              </GridItem>
            )}
            <GridItem>
              <SelectField
                name="department"
                label="createJob.department.label"
                placeholder={
                  job?.department ? '' : 'createJob.department.placeholder'
                }
                defaultvalue={job?.department ? true : false}
                fields={
                  job?.department && departments
                    ? [job.department].concat(
                        departments?.filter(
                          (dep) => dep.id !== job.department?.id,
                        ),
                      )
                    : (departments ?? [])
                }
                keys={(d) => d?.id ?? 0}
                labels={(d) => d?.name ?? ''}
                values={(d) => d?.id ?? 0}
              />
            </GridItem>
            <GridItem>
              <SelectField
                name="project"
                label="createJob.project.label"
                defaultvalue={true}
                fields={
                  projects && project
                    ? [project].concat(
                        projects.filter((pr) => pr.id !== project.id),
                      )
                    : []
                }
                keys={(p) => p.id}
                labels={(p) => p.name}
                values={(p) => p.id}
              />
            </GridItem>
          </Stack>
          <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
            <ImageList initialImages={imageList ? imageList : []}></ImageList>
          </div>
        </SimpleGrid>
      </DefaultModalDialog>
      <Paper
        w="full"
        h="full"
        transitionDuration="1000"
        _hover={{ shadow: 'xl', w: 'full', g: 'full' }}
      >
        <Flex justify="space-between">
          <Heading fontSize="large">{project?.name}</Heading>
          <SimpleGrid columns={2} gap={3}>
            <Tooltip label={translate('dashboard.newjobs.inspect')}>
              <Box cursor="pointer" onClick={modalDisclosure.onOpen}>
                <ViewIcon color="green.600" />
              </Box>
            </Tooltip>

            <Tooltip
              label={translate('dashboard.newjobs.delete')}
              aria-label={translate('dashboard.newjobs.delete')}
            >
              <Box
                _hover={{ cursor: 'pointer' }}
                onClick={() => {
                  void deleteNewJob();
                }}
              >
                {isDeleting ? (
                  <Spinner boxSize="20px" color="grey.600" thickness="2px" />
                ) : (
                  <DeleteIcon color="red.600" />
                )}
              </Box>
            </Tooltip>
          </SimpleGrid>
        </Flex>
        <LabeledText spaceBetween label="dashboard.newjobs.name">
          {record.new_job.name}
        </LabeledText>
        <LabeledText spaceBetween label="dashboard.newjobs.employee">
          {formatName(employee)}
        </LabeledText>
        <LabeledText spaceBetween label="dashboard.newjobs.date">
          {formatTimestamp(record.end)}
        </LabeledText>
        {record.comment && (
          <LabeledText col label="dashboard.newjobs.description">
            {record.comment}
          </LabeledText>
        )}
      </Paper>
    </>
  );
};
